import moment from "moment";

/** 多账期明细中按月的后端枚举值。 */
export const SETTLE_PERIOD_WAY_MONTH = 1;

/** 多账期明细中按天的后端枚举值。 */
export const SETTLE_PERIOD_WAY_DAY = 2;

/** 保证金转货款时，订单保证金参与应收/应付总额扣减。 */
const DEPOSIT_TRANSFER_YES = 1;

/** 每个结算单最多支持的账期明细数量。 */
export const MAX_SETTLE_PERIOD_ITEM_COUNT = 10;

/** 结算多账期明细的最小展示/计算结构。 */
export interface SettlePeriodItemLike {
    settlePeriodWay?: number;
    settlePeriod?: number;
    ratio?: number | string;
    amount?: number | string;
    settleEndDate?: string;
    sort?: number;
    calculationSource?: "ratio" | "amount";
}

/** 应收/应付总额计算依赖的金额字段。 */
export interface SettlementPayableAmountInput {
    actualAmount?: number | string | null;
    /** 结算预付款/预收款，按 3839 最新口径参与应收/应付总额扣减。 */
    settlementPayment?: number | string | null;
    /** 历史预结算金额字段，仅保留给旧调用方兜底，新业务优先使用 settlementPayment。 */
    preReconciliationAmount?: number | string | null;
    depositTransferAmount?: number | string | null;
    orderDeposit?: number | string | null;
}

/** 保证金转货款提交前校验依赖的金额字段。 */
export interface SettlementDepositValidationInput {
    depositTransferAmount?: number | string | null;
    reconciliationAmount?: number | string | null;
    preReconciliationAmount?: number | string | null;
    orderDeposit?: number | string | null;
    shouldValidatePreReconciliationAmount?: boolean;
}

/** 由旧单字段补齐多账期表单行时需要的最小输入。 */
export interface LegacySettlePeriodItemInput {
    settlePeriodWay?: number | string | null;
    settlePeriod?: number | string | null;
    payableAmount: number | string;
}

/** 前端预览账期截止日期时需要的最小输入。 */
export interface SettleEndDateInput {
    startDate?: string | number | Date | null;
    settlePeriodWay?: number | string | null;
    settlePeriod?: number | string | null;
}

/** 详情页账期展示的最小输入结构。 */
export interface SettlePeriodDisplayInput {
    settlePeriodItems?: SettlePeriodItemLike[] | null;
    reconciliation?: {
        settlePeriodWay?: number | null;
        settlePeriod?: number | null;
    } | null;
}

/** 表格操作失败时返回原明细和业务提示。 */
export interface SettlePeriodItemsChangeResult<T extends SettlePeriodItemLike> {
    items: T[];
    error?: string;
}

/** 账期表提交前的校验错误。 */
export interface SettlePeriodValidationError {
    code:
        | "REQUIRED"
        | "ITEM_COUNT_EXCEEDED"
        | "PAYABLE_AMOUNT_NEGATIVE"
        | "SETTLE_PERIOD_INTEGER_INVALID"
        | "SETTLE_PERIOD_RANGE_INVALID"
        | "RATIO_RANGE_INVALID"
        | "RATIO_TOTAL_NOT_100"
        | "AMOUNT_TOTAL_NOT_MATCH"
        | "SETTLE_END_DATE_DUPLICATED";
    message: string;
    rowIndex?: number;
    field?: keyof SettlePeriodItemLike;
}

const SETTLE_PERIOD_FIELD_REQUIRED_MESSAGE: Partial<Record<keyof SettlePeriodItemLike, string>> = {
    settlePeriodWay: "请选择账期类型",
    settlePeriod: "请输入账期值",
    ratio: "请输入比例",
    amount: "请输入金额"
};

/** 保证金转货款提交前的校验错误。 */
export interface SettlementDepositValidationError {
    code: "RECONCILIATION_AMOUNT_LESS_THAN_DEPOSIT" | "PRE_RECONCILIATION_AMOUNT_LESS_THAN_DEPOSIT";
    message: string;
}

/** 将后端可能返回的字符串金额安全转为数字，空值和非法值按 0 处理。 */
const toFiniteNumber = (value: number | string | null | undefined) => {
    if (value === undefined || value === null || value === "") return 0;
    const numberValue = Number(value);
    return Number.isFinite(numberValue) ? numberValue : 0;
};

/** 判断后端旧字段是否明确返回了值，避免把 0 天误判为无配置。 */
export const hasSettlePeriodValue = (value: number | string | null | undefined) => {
    return value !== undefined && value !== null && String(value).trim() !== "";
};

/** 将后端旧字段转为数字，非法值保持为空交由兜底逻辑处理。 */
const toOptionalFiniteNumber = (value: number | string | null | undefined) => {
    if (!hasSettlePeriodValue(value)) return undefined;
    const numberValue = Number(value);
    return Number.isFinite(numberValue) ? numberValue : undefined;
};

/** 兼容旧接口可能返回的“按天/按月”展示文案。 */
const toOptionalSettlePeriodWay = (value: number | string | null | undefined) => {
    const numberValue = toOptionalFiniteNumber(value);
    if (numberValue !== undefined) return numberValue;
    const textValue = String(value ?? "").trim();
    if (textValue.includes("月") || /month/i.test(textValue)) return SETTLE_PERIOD_WAY_MONTH;
    if (textValue.includes("天") || /day/i.test(textValue)) return SETTLE_PERIOD_WAY_DAY;
    return undefined;
};

/** 兼容旧接口可能返回的“3个月/12天”账期文案。 */
const toOptionalSettlePeriodNumber = (value: number | string | null | undefined) => {
    const numberValue = toOptionalFiniteNumber(value);
    if (numberValue !== undefined) return numberValue;
    const match = String(value ?? "")
        .trim()
        .match(/^-?\d+(\.\d+)?/);
    return match ? Number(match[0]) : undefined;
};

/** 金额和比例按产品口径统一保留两位小数。 */
const roundToTwo = (value: number) => {
    return Number((Math.round((value + Number.EPSILON) * 100) / 100).toFixed(2));
};

/** 金额展示使用千分位和两位小数，供详情页多账期文案复用。 */
const formatMoney = (value: number | string | undefined) => {
    return toFiniteNumber(value).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        useGrouping: true
    });
};

/** 账期单位按后端枚举转为详情页展示文案。 */
export const getSettlePeriodUnit = (settlePeriodWay?: number | null) => {
    return settlePeriodWay === SETTLE_PERIOD_WAY_MONTH ? "个月" : "天";
};

/**
 * 计算多账期拆分的应收/应付总额。
 * 公式：实际结算金额 - 结算预付款/预收款 - 保证金转货款时的订单保证金。
 */
export const calculateSettlementPayableAmount = ({
    actualAmount,
    settlementPayment,
    preReconciliationAmount,
    depositTransferAmount,
    orderDeposit
}: SettlementPayableAmountInput) => {
    const settlementPaymentDeduction = settlementPayment ?? preReconciliationAmount;
    const depositDeduction = Number(depositTransferAmount) === DEPOSIT_TRANSFER_YES ? toFiniteNumber(orderDeposit) : 0;
    return roundToTwo(toFiniteNumber(actualAmount) - toFiniteNumber(settlementPaymentDeduction) - depositDeduction);
};

/** 校验实际结算金额/预结算金额是否满足订单保证金转货款的后端口径。 */
export const validateSettlementDepositAmount = ({
    depositTransferAmount,
    reconciliationAmount,
    preReconciliationAmount,
    orderDeposit,
    shouldValidatePreReconciliationAmount
}: SettlementDepositValidationInput): SettlementDepositValidationError | undefined => {
    if (Number(depositTransferAmount) !== DEPOSIT_TRANSFER_YES) return undefined;
    const orderDepositValue = toFiniteNumber(orderDeposit);
    if (toFiniteNumber(reconciliationAmount) < orderDepositValue) {
        return { code: "RECONCILIATION_AMOUNT_LESS_THAN_DEPOSIT", message: "结算金额小于订单保证金" };
    }
    if (shouldValidatePreReconciliationAmount && toFiniteNumber(preReconciliationAmount) < orderDepositValue) {
        return { code: "PRE_RECONCILIATION_AMOUNT_LESS_THAN_DEPOSIT", message: "结算金额小于订单保证金" };
    }
    return undefined;
};

/** 按比例计算当前账期金额。 */
export const calculateSettlePeriodAmountByRatio = (payableAmount: number | string, ratio: number | string) => {
    return roundToTwo((toFiniteNumber(payableAmount) * toFiniteNumber(ratio)) / 100);
};

/** 按金额反算当前账期比例。 */
export const calculateSettlePeriodRatioByAmount = (payableAmount: number | string, amount: number | string) => {
    const payableAmountValue = toFiniteNumber(payableAmount);
    if (payableAmountValue === 0) return 0;
    return roundToTwo((toFiniteNumber(amount) / payableAmountValue) * 100);
};

/** 按项目固定账期生成默认明细，供新增结算页初始化使用。 */
export const createFixedProjectSettlePeriodItem = ({
    settlePeriodWay,
    settlePeriod,
    payableAmount
}: {
    settlePeriodWay: number;
    settlePeriod: number;
    payableAmount: number | string;
}) => {
    return {
        settlePeriodWay,
        settlePeriod,
        ratio: 100,
        amount: roundToTwo(toFiniteNumber(payableAmount)),
        calculationSource: "ratio" as const
    };
};

/** 历史结算单只有 settlePeriod/settlePeriodWay 时，转换成新多账期表格的一行 100% 明细。 */
export const createLegacySettlePeriodItem = ({
    settlePeriodWay,
    settlePeriod,
    payableAmount
}: LegacySettlePeriodItemInput) => {
    const settlePeriodWayValue = toOptionalSettlePeriodWay(settlePeriodWay);
    const settlePeriodValue = toOptionalSettlePeriodNumber(settlePeriod);
    if (settlePeriodWayValue === undefined || settlePeriodValue === undefined) return undefined;
    return {
        ...createFixedProjectSettlePeriodItem({
            settlePeriodWay: settlePeriodWayValue,
            settlePeriod: settlePeriodValue,
            payableAmount
        }),
        sort: 1
    };
};

/** 按业务起算日期和账期实时预览截止日期，按月规则与后端月末溢出口径一致。 */
export const calculateSettleEndDate = ({ startDate, settlePeriodWay, settlePeriod }: SettleEndDateInput) => {
    if (!startDate) return "";
    const settlePeriodWayValue = toOptionalSettlePeriodWay(settlePeriodWay);
    const settlePeriodValue = toOptionalSettlePeriodNumber(settlePeriod);
    if (settlePeriodWayValue === undefined || settlePeriodValue === undefined) return "";

    const startMoment = moment(startDate);
    if (!startMoment.isValid()) return "";
    if (settlePeriodValue <= 0) return startMoment.format("YYYY-MM-DDT00:00:00");

    if (settlePeriodWayValue === SETTLE_PERIOD_WAY_MONTH) {
        const settleEndMoment = startMoment.clone().add(settlePeriodValue, "months");
        if (settleEndMoment.date() < startMoment.date()) {
            return settleEndMoment.endOf("month").add(1, "day").format("YYYY-MM-DDT00:00:00");
        }
        return settleEndMoment.subtract(1, "day").format("YYYY-MM-DDT00:00:00");
    }

    const settleEndMoment = startMoment.clone().add(settlePeriodValue, "days");
    return settleEndMoment.format("YYYY-MM-DDT00:00:00");
};

/** 新增一行账期明细，超过 10 行时返回原列表和业务错误。 */
export const addSettlePeriodItem = <T extends SettlePeriodItemLike>(
    items: T[],
    newItem: T = {} as T
): SettlePeriodItemsChangeResult<T> => {
    if (items.length >= MAX_SETTLE_PERIOD_ITEM_COUNT) {
        return { items, error: "最多支持10个账期" };
    }
    return { items: [...items, newItem] };
};

/** 删除一行账期明细，至少保留 1 行。 */
export const removeSettlePeriodItem = <T extends SettlePeriodItemLike>(
    items: T[],
    index: number
): SettlePeriodItemsChangeResult<T> => {
    if (items.length <= 1) {
        return { items, error: "至少保留1个账期" };
    }
    return { items: items.filter((_, itemIndex) => itemIndex !== index) };
};

/** 更新当前行比例，并按应收/应付总额联动金额。 */
export const updateSettlePeriodItemRatio = <T extends SettlePeriodItemLike>(
    items: T[],
    index: number,
    ratio: number | string,
    payableAmount: number | string
) => {
    return items.map((item, itemIndex) => {
        if (itemIndex !== index) return item;
        return {
            ...item,
            ratio: roundToTwo(toFiniteNumber(ratio)),
            amount: calculateSettlePeriodAmountByRatio(payableAmount, ratio),
            calculationSource: "ratio" as const
        };
    });
};

/** 更新当前行金额，并按应收/应付总额反算比例。 */
export const updateSettlePeriodItemAmount = <T extends SettlePeriodItemLike>(
    items: T[],
    index: number,
    amount: number | string,
    payableAmount: number | string
) => {
    return items.map((item, itemIndex) => {
        if (itemIndex !== index) return item;
        return {
            ...item,
            amount: roundToTwo(toFiniteNumber(amount)),
            ratio: calculateSettlePeriodRatioByAmount(payableAmount, amount),
            calculationSource: "amount" as const
        };
    });
};

/** 提交前校验多账期必填、行数、比例合计、金额合计和截止日期重复。 */
export const validateSettlePeriodItems = (items: SettlePeriodItemLike[], payableAmount: number | string) => {
    const errors: SettlePeriodValidationError[] = [];
    const payableAmountValue = roundToTwo(toFiniteNumber(payableAmount));
    let hasItemValueError = false;

    if (payableAmountValue < 0) {
        errors.push({ code: "PAYABLE_AMOUNT_NEGATIVE", message: "扣减后金额不能小于 0" });
    }

    if (items.length > MAX_SETTLE_PERIOD_ITEM_COUNT) {
        errors.push({ code: "ITEM_COUNT_EXCEEDED", message: "最多支持10个账期" });
    }

    items.forEach((item, index) => {
        const requiredFields: Array<keyof SettlePeriodItemLike> = ["settlePeriodWay", "settlePeriod", "ratio", "amount"];
        for (const field of requiredFields) {
            if (item[field] === undefined || item[field] === null || item[field] === "") {
                errors.push({
                    code: "REQUIRED",
                    message: SETTLE_PERIOD_FIELD_REQUIRED_MESSAGE[field] ?? "请输入账期明细",
                    rowIndex: index,
                    field
                });
            }
        }
        if (hasSettlePeriodValue(item.settlePeriod)) {
            const settlePeriodValue = Number(item.settlePeriod);
            // 账期值直接影响截止日期和后端应收/应付生成，提交前必须逐行校验边界。
            if (!Number.isInteger(settlePeriodValue)) {
                hasItemValueError = true;
                errors.push({ code: "SETTLE_PERIOD_INTEGER_INVALID", message: "请输入整数", rowIndex: index, field: "settlePeriod" });
            } else if (settlePeriodValue < 0 || settlePeriodValue > 1000) {
                hasItemValueError = true;
                errors.push({
                    code: "SETTLE_PERIOD_RANGE_INVALID",
                    message: "请输入0到1000之间的整数",
                    rowIndex: index,
                    field: "settlePeriod"
                });
            }
        }
        if (item.ratio !== undefined && item.ratio !== null && item.ratio !== "") {
            const ratioValue = Number(item.ratio);
            // 单行比例允许小数，但必须大于 0 且不超过 100，避免负数和超比例互相抵消后绕过合计校验。
            if (!Number.isFinite(ratioValue) || ratioValue <= 0 || ratioValue > 100) {
                hasItemValueError = true;
                errors.push({ code: "RATIO_RANGE_INVALID", message: "请输入大于0且小于等于100的比例", rowIndex: index, field: "ratio" });
            }
        }
    });

    if (!hasItemValueError) {
        const ratioTotal = roundToTwo(items.reduce((total, item) => total + toFiniteNumber(item.ratio), 0));
        if (ratioTotal !== 100) {
            errors.push({ code: "RATIO_TOTAL_NOT_100", message: "请确保所有账期比例合计等于100%" });
        }

        const amountTotal = roundToTwo(items.reduce((total, item) => total + toFiniteNumber(item.amount), 0));
        if (amountTotal !== payableAmountValue) {
            errors.push({ code: "AMOUNT_TOTAL_NOT_MATCH", message: "账期明细金额合计必须等于实际应收/应付总额" });
        }
    }

    const settleEndDateSet = new Set<string>();
    for (const item of items) {
        if (!item.settleEndDate) continue;
        const settleEndDate = item.settleEndDate.slice(0, 10);
        if (settleEndDateSet.has(settleEndDate)) {
            errors.push({ code: "SETTLE_END_DATE_DUPLICATED", message: "截止日期相等请调整" });
            break;
        }
        settleEndDateSet.add(settleEndDate);
    }

    return errors;
};

/** 账期明细展示前按 sort 升序稳定排序，缺失 sort 的明细保持原相对顺序靠后。 */
export const normalizeSettlePeriodItems = <T extends SettlePeriodItemLike>(items?: T[] | null) => {
    return [...(items ?? [])].sort((itemA, itemB) => {
        const sortA = itemA.sort ?? Number.MAX_SAFE_INTEGER;
        const sortB = itemB.sort ?? Number.MAX_SAFE_INTEGER;
        return sortA - sortB;
    });
};

/** 将单条账期格式化为“30天”或“2个月”。 */
export const formatSettlePeriodValue = (item: SettlePeriodItemLike) => {
    if (item.settlePeriod === undefined || item.settlePeriod === null) return "";
    return `${item.settlePeriod}${getSettlePeriodUnit(item.settlePeriodWay)}`;
};

/**
 * 详情页账期展示：
 * 多明细展示“30天300,000.00元，2个月700,000.00元”，单明细只展示账期值。
 */
export const formatSettlePeriodDisplay = ({ settlePeriodItems, reconciliation }: SettlePeriodDisplayInput) => {
    const normalizedItems = normalizeSettlePeriodItems(settlePeriodItems).filter(
        item => item.settlePeriod !== undefined && item.settlePeriod !== null
    );

    if (normalizedItems.length === 1) return formatSettlePeriodValue(normalizedItems[0]);
    if (normalizedItems.length > 1) {
        return normalizedItems
            .map(item => `${formatSettlePeriodValue(item)}${formatMoney(item.amount)}元`)
            .join("，");
    }

    if (reconciliation?.settlePeriod === undefined || reconciliation.settlePeriod === null) return "";
    return formatSettlePeriodValue({
        settlePeriodWay: reconciliation.settlePeriodWay ?? undefined,
        settlePeriod: reconciliation.settlePeriod
    });
};
